import * as React from 'react';
import * as styles from './styles.scss';
import { Icon, IIconStyles } from '@fluentui/react/lib/Icon';
import { observer } from 'mobx-react-lite';
import loc from 'owa-localize';
import { getPalette } from 'owa-theme';
import {
    assetErrorGeneric,
    assetErrorRemovedFromSource,
    assetErrorUnauthorized,
    assetErrorSelectionNotSupported,
} from './AssetError.locstring.json';
import { getAssetDescriptor } from '../assetsCache';
import useDeleteAsset from '../hooks/useDeleteAsset';
import AssetButton from './AssetButton';
import { removeFromCanvas, retry } from '../assetCommands/AssetCommandDescriptors';
import { logSpacesDatapoint } from 'owa-timestream-logging';

const iconStyles: Partial<IIconStyles> = {
    root: {
        fontSize: 16,
        color: getPalette().neutralSecondary,
        marginTop: 1,
        marginRight: 7,
    },
};

export enum AssetErrorType {
    NotFound,
    Unauthorized,
    Retryable,
    SelectionNotSupported,
}

export default observer(function AssetError({
    assetType,
    errorType,
    retryCallback,
}: {
    assetType: string;
    errorType?: AssetErrorType;
    retryCallback?: () => void;
}) {
    React.useEffect(() => {
        logSpacesDatapoint('OUTLOOKSPACES_ASSET_ERROR', {
            assetType_1: assetType,
            errorType_2: errorType,
        });
    }, [assetType, errorType]);

    const deleteAsset = useDeleteAsset();
    const logAndDeleteAsset = React.useCallback(() => {
        logSpacesDatapoint('OUTLOOKSPACES_ASSET_ERROR_DELETE', {
            assetType_1: assetType,
            errorType_2: errorType,
        });
        deleteAsset();
    }, [deleteAsset, assetType, errorType]);

    const assetDesc = getAssetDescriptor(assetType);
    const description = getDescription(errorType);
    return (
        <div className={styles.assetError}>
            <div className={styles.assetErrorHeader}>
                <Icon styles={iconStyles} iconName={assetDesc?.iconName} />
                <div className={styles.assetErrorHeaderText}>{assetDesc?.getErrorMessage()}</div>
            </div>
            <div className={styles.assetErrorDescription}>{description}</div>
            {(errorType === AssetErrorType.NotFound ||
                errorType === AssetErrorType.Unauthorized ||
                errorType === AssetErrorType.SelectionNotSupported) && (
                <AssetButton command={removeFromCanvas} onClick={logAndDeleteAsset} />
            )}
            {errorType === AssetErrorType.Retryable && !!retryCallback && (
                <AssetButton command={retry} onClick={retryCallback} />
            )}
        </div>
    );
});

function getDescription(errorType?: AssetErrorType): string {
    switch (errorType) {
        case AssetErrorType.NotFound:
            return loc(assetErrorRemovedFromSource);
        case AssetErrorType.Unauthorized:
            return loc(assetErrorUnauthorized);
        case AssetErrorType.SelectionNotSupported:
            return loc(assetErrorSelectionNotSupported);
        default:
            return loc(assetErrorGeneric);
    }
}
